import { useEffect, useState } from 'react'
import { Navigate } from 'react-router'
import { useAtomValue } from 'jotai'
import { authenticationState, useAuthentication } from '@repo/unicity'
import { LoadingLayout } from '../layout/loading.layout'
import { resolveDefaultUserRoute } from '@/core/auth/post-login-route.helpers'
import { userStatusAtom } from '@/core/auth/user-status.store'

interface GuestGuardProps {
    children: React.ReactNode
    /** Where to send authed users. Defaults to the user account page. */
    redirectTo?: string
}

/**
 * Inverse of AuthGuard: keeps authenticated users out of guest-only pages
 * (login, sign-up, reset-password, etc.). We restore the persisted session
 * once before deciding so a fresh refresh on /sign-up doesn't briefly
 * flash the form for a logged-in user.
 */
const GuestGuard = ({ children, redirectTo = '/user' }: GuestGuardProps) => {
    const authentication = useAtomValue(authenticationState)
    const userStatus = useAtomValue(userStatusAtom)
    const { restoreSession } = useAuthentication({ enablePersist: true })
    const [status, setStatus] = useState<'checking' | 'guest' | 'authed'>('checking')

    useEffect(() => {
        if (authentication.token && authentication.baId) {
            setStatus('authed')
            return
        }
        const restored = restoreSession()
        setStatus(restored ? 'authed' : 'guest')
        // eslint-disable-next-line react-hooks/exhaustive-deps -- session restore is a mount-once check; auth atoms/navigate intentionally excluded
    }, [])

    if (status === 'checking') return <LoadingLayout data-testid="guest-guard-loading-layout" />
    if (status === 'authed') {
        const target = redirectTo === '/user' ? resolveDefaultUserRoute(userStatus) : redirectTo
        return <Navigate to={target} replace data-testid="guest-guard-navigate" />
    }
    return <>{children}</>
}

export default GuestGuard
